import { useEffect, useId, useState, type FormEvent } from 'react';
import type { RedirectDto } from '@ecom/contracts';
import { FilterBar } from '@/components/filters';
import { ToggleField } from '@/components/fields';
import { ChevronRightIcon, PlusIcon, UploadIcon } from '@/components/icons';
import { ListCard, ListToolbar, LoadMore, Page, Sheet, Table, TableScroll, Td, Th, Tr } from '@/components/page';
import { Alert, Button, ConfirmDialog, EmptyState, Field, Input, Pill, TableSkeleton, Textarea } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { age, count } from '@/lib/format';
import { useRedirectsStore } from '@/stores/redirects.store';

/**
 * Redirects (ADR-073) — the store's old links, and where each goes now. A
 * store arriving from another platform pastes them in by the hundred; the
 * hits say which old links still bring people.
 */
export function RedirectsPage() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { items, nextCursor, total, search, loading, error, load, loadMore } = useRedirectsStore();
  const [editing, setEditing] = useState<RedirectDto | 'new' | null>(null);
  const [pasting, setPasting] = useState(false);

  useEffect(() => {
    void load();
  }, [load]);

  const actions = (
    <>
      <Button variant="secondary" onClick={() => setPasting(true)}>
        <UploadIcon className="size-4" />
        {t('redirectsBulk')}
      </Button>
      <Button onClick={() => setEditing('new')}>
        <PlusIcon className="size-4" />
        {t('addRedirect')}
      </Button>
    </>
  );

  return (
    <Page title={t('redirects')} subtitle={t('redirectsSubtitle')} actions={actions} fill>
      <ListCard>
        <ListToolbar>
          <FilterBar search={search} onSearch={(term) => void load(term)} placeholder={t('searchRedirects')} total={total ?? undefined} shown={items.length} />
        </ListToolbar>

        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading && items.length === 0 ? (
          <TableSkeleton columns={4} />
        ) : items.length === 0 ? (
          <EmptyState title={t('noRedirects')} body={t('noRedirectsHint')} icon={<ChevronRightIcon />} action={actions} />
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t('redirectFrom')}</Th>
                  <Th>{t('redirectTo')}</Th>
                  <Th>{t('redirectKind')}</Th>
                  <Th align="end">{t('redirectHits')}</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((redirect) => (
                  <Tr key={redirect.id} onClick={() => setEditing(redirect)}>
                    <Td>
                      {/* A link reads left to right whatever the page around it; <bdi> keeps the cell's alignment. */}
                      <span className="block max-w-72 truncate font-medium text-ink">
                        <bdi dir="ltr">{redirect.fromPath}</bdi>
                      </span>
                    </Td>
                    <Td>
                      <span className="block max-w-72 truncate text-ink-muted">
                        <bdi dir="ltr">{redirect.toPath}</bdi>
                      </span>
                    </Td>
                    <Td>
                      <Pill tone={redirect.permanent ? 'neutral' : 'info'}>{redirect.permanent ? t('redirectPermanent') : t('redirectTemporary')}</Pill>
                    </Td>
                    <Td align="end" className="whitespace-nowrap">
                      <span data-numeric className="block text-ink">
                        {count(redirect.hits, locale)}
                      </span>
                      {redirect.lastHitAt ? <span className="block text-xs text-ink-muted">{age(redirect.lastHitAt, locale)}</span> : null}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}

        <LoadMore visible={Boolean(nextCursor)} loading={loading} onClick={() => void loadMore()} />
      </ListCard>

      {editing ? (
        <RedirectSheet key={editing === 'new' ? 'new' : editing.id} redirect={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />
      ) : null}
      {pasting ? <BulkSheet onDone={() => setPasting(false)} /> : null}
    </Page>
  );
}

function RedirectSheet({ redirect, onDone }: { redirect: RedirectDto | null; onDone: () => void }) {
  const t = useT();
  const formId = useId();
  const { saving, saveError, save, remove, clearSaveError } = useRedirectsStore();
  const [fromPath, setFromPath] = useState(redirect?.fromPath ?? '');
  const [toPath, setToPath] = useState(redirect?.toPath ?? '');
  const [permanent, setPermanent] = useState(redirect?.permanent ?? true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    clearSaveError();
  }, [clearSaveError]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (await save(redirect?.id ?? null, { fromPath, toPath, permanent })) onDone();
  }

  return (
    <Sheet
      open
      title={redirect ? t('editRedirect') : t('addRedirect')}
      subtitle={t('redirectEditorHint')}
      onClose={onDone}
      footer={
        <>
          {redirect ? (
            <Button variant="ghost" className="me-auto text-danger" onClick={() => setDeleting(true)} disabled={saving}>
              {t('delete')}
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onDone} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button type="submit" form={formId} disabled={saving || !fromPath.trim() || !toPath.trim()}>
            {saving ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} noValidate className="flex flex-col gap-5">
        <Field label={t('redirectFrom')} htmlFor="redirect-from" hint={t('redirectFromHint')}>
          <Input id="redirect-from" dir="ltr" value={fromPath} onChange={(event) => setFromPath(event.target.value)} placeholder="/ar/…" />
        </Field>
        <Field label={t('redirectTo')} htmlFor="redirect-to" hint={t('redirectToHint')}>
          <Input id="redirect-to" dir="ltr" value={toPath} onChange={(event) => setToPath(event.target.value)} placeholder="/products/…" />
        </Field>
        <ToggleField label={t('redirectPermanentLabel')} hint={t('redirectPermanentHint')} checked={permanent} onChange={setPermanent} />
        {saveError ? <Alert>{saveError}</Alert> : null}
      </form>

      <ConfirmDialog
        open={deleting}
        title={t('deleteRedirectTitle')}
        body={t('deleteRedirectBody')}
        confirmLabel={t('delete')}
        cancelLabel={t('cancel')}
        busy={saving}
        onConfirm={async () => {
          if (redirect && (await remove(redirect.id))) onDone();
          setDeleting(false);
        }}
        onCancel={() => setDeleting(false)}
      />
    </Sheet>
  );
}

/** Many at once: one pair a line, "old, new" — each added, updated or refused with its reason. */
function BulkSheet({ onDone }: { onDone: () => void }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const formId = useId();
  const { saving, saveError, bulkResult, bulk, clearSaveError } = useRedirectsStore();
  const [text, setText] = useState('');
  const [permanent, setPermanent] = useState(true);

  useEffect(() => {
    clearSaveError();
  }, [clearSaveError]);

  return (
    <Sheet
      open
      size="lg"
      title={t('redirectsBulk')}
      subtitle={t('redirectsBulkHint')}
      onClose={onDone}
      footer={
        <>
          <Button variant="secondary" onClick={onDone} disabled={saving}>
            {bulkResult ? t('close') : t('cancel')}
          </Button>
          <Button type="submit" form={formId} disabled={saving || !text.trim()}>
            {saving ? t('saving') : t('redirectsBulkAdd')}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(event) => {
          event.preventDefault();
          void bulk(text, permanent);
        }}
        className="flex flex-col gap-5"
      >
        <Field label={t('redirectsBulkLines')} htmlFor="redirect-lines" hint={t('redirectsBulkLinesHint')}>
          <Textarea
            id="redirect-lines"
            dir="ltr"
            rows={12}
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={'/old-wallet, /products/bifold-wallet-brown\n/ar/حقيبة/p123, /products/backpack-daily'}
            className="font-mono text-xs"
          />
        </Field>
        <ToggleField label={t('redirectPermanentLabel')} hint={t('redirectPermanentHint')} checked={permanent} onChange={setPermanent} />

        {bulkResult ? (
          <Alert tone={bulkResult.refused.length > 0 ? 'info' : 'success'}>
            {t('redirectsBulkResult', {
              added: count(bulkResult.added, locale),
              updated: count(bulkResult.updated, locale),
              refused: count(bulkResult.refused.length, locale),
            })}
          </Alert>
        ) : null}
        {bulkResult && bulkResult.refused.length > 0 ? (
          <ul className="flex flex-col divide-y divide-line rounded-control border border-line text-sm">
            {bulkResult.refused.map((line) => (
              <li key={line.line} className="flex flex-col gap-0.5 px-3 py-2">
                <span className="text-xs text-ink-muted">
                  {t('redirectsBulkLine', { line: count(line.line, locale) })} · <bdi dir="ltr">{line.text}</bdi>
                </span>
                <span className="text-ink">{line.message}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {saveError ? <Alert>{saveError}</Alert> : null}
      </form>
    </Sheet>
  );
}
